<template>
  <div
    class="help-renderer"
    :class="{ 'help-renderer--standalone': !showNav }"
    data-testid="help-renderer"
  >
    <nav
      v-if="showNav"
      class="help-nav"
      data-testid="help-nav"
      aria-label="Help topics"
    >
      <p class="help-nav__heading">{{ navHeading }}</p>
      <ul class="help-nav__list">
        <li v-for="entry in articles" :key="entry.slug">
          <router-link
            :to="articlePath(entry.slug)"
            class="help-nav__link"
            :class="{ 'help-nav__link--active': entry.slug === activeSlug }"
            :data-testid="`help-nav-link-${entry.slug}`"
          >
            {{ entry.title }}
          </router-link>
        </li>
      </ul>
    </nav>

    <div class="help-main">
      <nav
        v-if="showBreadcrumb"
        class="help-breadcrumb"
        data-testid="help-breadcrumb"
        aria-label="Breadcrumb"
      >
        <router-link :to="indexPath" data-testid="help-breadcrumb-root">
          Help
        </router-link>
        <template v-if="article">
          <span class="help-breadcrumb__sep" aria-hidden="true">/</span>
          <span data-testid="help-breadcrumb-current">{{ article.title }}</span>
        </template>
      </nav>

      <div v-if="loading" class="help-state" data-testid="help-loading">
        Loading help...
      </div>

      <div
        v-else-if="notFound"
        class="help-state"
        data-testid="help-not-found"
      >
        <h1>We could not find that help article</h1>
        <p>
          It may have been renamed or removed.
          <!--
            Standalone there is no nav and no breadcrumb, so without this
            link a 404 is a total dead end -- the copy tells the reader to
            pick another topic, so it has to give them somewhere to pick
            from. Framed, this is a harmless second route to the index.
          -->
          <router-link
            :to="indexPath"
            data-testid="help-not-found-index-link"
          >
            Browse all help topics
          </router-link>
          to keep going.
        </p>
      </div>

      <div v-else-if="failed" class="help-state" data-testid="help-error">
        <h1>Help is unavailable right now</h1>
        <p>Please try again in a moment.</p>
      </div>

      <!-- Index view: no slug in the route, so show the topic list. -->
      <div v-else-if="!activeSlug" class="help-index" data-testid="help-index">
        <h1>{{ navHeading }}</h1>
        <ul class="help-index__list">
          <li v-for="entry in articles" :key="entry.slug">
            <router-link
              :to="articlePath(entry.slug)"
              :data-testid="`help-index-link-${entry.slug}`"
            >
              {{ entry.title }}
            </router-link>
            <p v-if="entry.summary" class="help-index__summary">
              {{ entry.summary }}
            </p>
          </li>
        </ul>
        <p v-if="!articles.length" data-testid="help-index-empty">
          Help articles are on their way.
        </p>
      </div>

      <article v-else class="help-article" data-testid="help-article">
        <h1 class="help-article__title">{{ article.title }}</h1>
        <p v-if="article.summary" class="help-article__summary">
          {{ article.summary }}
        </p>
        <!--
          v-html sink. The payload is sanitized SERVER-side with nh3 and
          again CLIENT-side with DOMPurify (sanitizeHelpHtml) immediately
          below. Two independent boundaries because this is the one place
          help content becomes live markup.
        -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          ref="body"
          class="help-article__body"
          data-testid="help-article-body"
          v-html="safeHtml"
        ></div>
      </article>
    </div>
  </div>
</template>

<script>
/**
 * HelpRenderer -- the ONE shared help component.
 *
 * Per `docs/user/HELP-SYSTEM-DESIGN.md` decision 4, every help surface
 * reuses this component; surfaces differ ONLY in which route group mounts
 * them and which audience they pass in. There is deliberately no
 * owner-specific or shopper-specific variant: a second copy would drift,
 * and the design calls this out explicitly.
 *
 * Routes derive from FILES, not from a hand-maintained slug table -- the
 * component takes whatever `:slug` the route supplies and resolves it
 * against the API, so adding a markdown file under `docs/user/<audience>/`
 * publishes a page with no frontend change (design decision 2 + 3).
 *
 * THE PRESENTATION RULE (Jake, browser review of the shipped surface)
 * ------------------------------------------------------------------
 * One route renders in three presentations, and they must differ. Two
 * INDEPENDENT axes decide what appears; collapsing them into one is what
 * produced the duplicated index this rule replaced.
 *
 *   route        -> what the CONTENT is    (index list | article body)
 *   presentation -> how much CHROME wraps it
 *                   (framed = navigational | standalone = static document)
 *
 *   | element       | index framed | index alone | art. framed | art. alone |
 *   | sidebar nav   | no           | no          | YES         | no         |
 *   | breadcrumb    | no           | no          | YES         | no         |
 *   | index heading | YES          | YES         | n/a         | n/a        |
 *   | index list    | YES          | YES         | n/a         | n/a        |
 *   | article title | n/a          | n/a         | YES         | YES        |
 *   | article body  | n/a          | n/a         | YES         | YES        |
 *
 * Read it as: CONTENT always renders; NAVIGATION renders only in the
 * flyover, and only where it points somewhere the reader is not already.
 *
 *  1. FRAMED (`?framed=1`, the flyover iframe) -- navigational. This is
 *     where a reader browses between articles, so an article keeps the
 *     sidebar and the breadcrumb.
 *  2. STANDALONE (no flag, a new tab / bookmark / direct link) -- a static
 *     document. Jake: "we don't need all of the nav links. The intent is
 *     to have the stand-alone help page read like a static document."
 *     Title and body only. The one exception is the not-found state,
 *     which keeps an index link because with no other chrome a 404 would
 *     otherwise be a dead end.
 *  3. INDEX (no slug, either presentation) -- keeps its list, because an
 *     index stripped of its list is an empty page. It never shows the
 *     sidebar (the same links again, minus the summaries) or the
 *     breadcrumb (a crumb to the page you are on).
 *
 * WHY ONE COMPONENT AND NOT THREE. The three differ by two booleans over
 * a shared body, not by structure -- every presentation renders the same
 * fetched article through the same sanitizing sink. Splitting them would
 * fork that sink three ways and reintroduce exactly the drift design
 * decision 4 exists to prevent. If a future presentation needs different
 * CONTENT rather than different chrome, revisit that.
 */
import api from "@/services/api";
import { isHelpFramed } from "@/router/helpFramed";
import { sanitizeHelpHtml } from "@/utils/sanitizeHelpHtml";
import { resolveHelpConditions } from "@/utils/resolveHelpConditions";
import { labelHelpSections } from "@/utils/labelHelpSections";
import {
  fetchEffectiveFeeRate,
  fetchHelpContext,
} from "@/services/helpContextService";


const NAV_HEADINGS = {
  owner: "Help",
  shopper: "Help",
  superadmin: "Platform help",
};

/**
 * The charset the server's heading slugs are restricted to
 * (`slugify_heading` in `api/src/utils/help_markdown.py`).
 *
 * The route hash reaches a DOM lookup, so it is validated against this
 * rather than handed to `querySelector` as an arbitrary selector -- a hash
 * is attacker-suppliable (it is just the URL), and `#a"],script` would
 * otherwise be interpreted as selector syntax. Matching the server's own
 * charset means every legitimate anchor passes and nothing else does.
 */
const HEADING_SLUG_RE = /^[a-z0-9-]+$/;

export default {
  name: "HelpRenderer",

  props: {
    /**
     * Which audience tree to serve from: 'owner' | 'shopper' | 'superadmin'.
     * Supplied by the mounting route group, never by the URL, so a shopper
     * surface cannot be steered at the owner tree by editing the address bar.
     */
    audience: {
      type: String,
      required: true,
      validator: (value) =>
        ["owner", "shopper", "superadmin"].includes(value),
    },
    /**
     * Base path this surface is mounted at (e.g. '/distributor/help/owner'
     * or '/help/shopper'). Used to build nav + breadcrumb links so the same
     * component produces correct URLs on either surface.
     */
    basePath: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      articles: [],
      article: null,
      loading: true,
      notFound: false,
      failed: false,
      // The `/help/context` payload (global settings conditional text
      // branches on), or null when it could not be read. `null` means
      // "unknown", which resolves every condition to its neutral text. The
      // owner-scoped effective fee rate (plan Step B4) is merged into this
      // object as `platform_fee_rate` for the owner audience; the resolver
      // reads any key it finds.
      helpContext: null,
    };
  },

  computed: {
    activeSlug() {
      return this.$route?.params?.slug || "";
    },

    navHeading() {
      return NAV_HEADINGS[this.audience] || "Help";
    },

    /**
     * True when this route is rendering inside the help flyover's iframe.
     *
     * Reads the EXISTING `?framed=1` seam through `isHelpFramed` rather
     * than sniffing the query directly, so the flag keeps exactly one
     * definition and one prefix guard. This is a read; it does not widen
     * what the flag does. The flag still only chooses presentation and
     * still grants nothing -- see `router/helpFramed.js`.
     */
    framed() {
      return isHelpFramed(this.$route);
    },

    /**
     * The sidebar list of sibling articles: navigation, not content.
     *
     * Shown in exactly one of the three presentations -- a framed article
     * -- because that is the only one where moving between articles is the
     * point. On the INDEX it would duplicate the body list (Jake: "the
     * index page has redundancies"), and STANDALONE it is the "nav links"
     * a static document does not want.
     */
    showNav() {
      return this.framed && !!this.activeSlug && this.articles.length > 0;
    },

    /**
     * The breadcrumb -- a deliberate call, not a reflex deletion.
     *
     * On an ARTICLE it points at the index, a real parent, and framed it
     * is the reader's only labelled way back, so it stays there. On the
     * INDEX its sole crumb links to the page already on screen, which is
     * the stray "Help" above the list in Jake's screenshot -- never
     * useful, so it goes in both presentations. STANDALONE it is chrome
     * like any other nav, and the article title carries the meaning.
     */
    showBreadcrumb() {
      return this.framed && !!this.activeSlug;
    },

    indexPath() {
      return this.basePath;
    },

    safeHtml() {
      // Order matters: sanitize FIRST, then resolve, then label. The
      // resolver only removes nodes and writes text, and the labeler only
      // adds test ids it generates itself, so the sanitizer stays the last
      // word on which tags exist.
      return labelHelpSections(
        resolveHelpConditions(
          sanitizeHelpHtml(this.article?.html || ""),
          this.helpContext
        )
      );
    },
  },

  watch: {
    // The route param drives the fetch, so in-surface nav between articles
    // re-resolves without remounting.
    activeSlug: "loadArticle",
    audience: "loadAll",

    /**
     * Same-article fragment changes (`#a` -> `#b`) do not refetch, so the
     * scroll has to be driven from the hash as well as from the load.
     */
    "$route.hash": "scrollToHash",
  },

  created() {
    this.loadAll();
  },

  methods: {
    articlePath(slug) {
      return `${this.basePath}/${slug}`;
    },

    /**
     * Scroll the heading named by the route hash into view.
     *
     * WHY THIS IS NEEDED AT ALL. Two things stop a `#fragment` working on
     * its own here, and either one alone would be enough to break it:
     *
     *   1. The router is created with NO `scrollBehavior`
     *      (`router/index.js`), so Vue Router does not act on a hash.
     *   2. The article body is FETCHED. On a cold load the browser
     *      resolves the fragment while the body is still an empty div, so
     *      even native behaviour would find nothing to scroll to.
     *
     * So the scroll is driven from the point the body actually exists --
     * after `loadArticle` resolves and Vue has patched the DOM.
     *
     * WHY `scrollIntoView` ON THE ELEMENT rather than setting
     * `window.location.hash`: this component renders BOTH standalone and
     * inside the flyover's iframe. Scrolling an element is an in-document
     * operation that behaves identically in either, whereas writing to
     * `location.hash` inside a frame pushes an entry into the parent's
     * session history -- so the reader's Back button would start undoing
     * scroll positions in a panel they cannot see.
     */
    scrollToHash() {
      const raw = this.$route?.hash || "";
      if (!raw) return;

      const id = raw.replace(/^#/, "");
      // Reject anything outside the server's slug charset before it
      // reaches a DOM query -- see HEADING_SLUG_RE.
      if (!HEADING_SLUG_RE.test(id)) return;

      this.$nextTick(() => {
        const root = this.$refs.body;
        // A stale deep link (heading since renamed) finds nothing; the
        // reader simply lands at the top rather than hitting an error.
        const target = root?.querySelector?.(`#${id}`);
        if (!target) return;
        // A heading inside a COLLAPSED section is not rendered, so scrolling
        // to it would land nowhere: open its section first. A heading in a
        // section's <summary> is always visible and opens nothing.
        const section = target.closest("details");
        if (section && !target.closest("summary")) section.open = true;
        target.scrollIntoView?.({ block: "start" });
      });
    },

    async loadAll() {
      await Promise.all([this.loadIndex(), this.loadArticle()]);
    },

    async loadIndex() {
      try {
        const { data } = await api.get(`/help/${this.audience}`);
        this.articles = Array.isArray(data?.articles) ? data.articles : [];
      } catch (error) {
        // A missing nav must not blank the article body.
        this.articles = [];
      }
    },

    async loadArticle() {
      this.notFound = false;
      this.failed = false;

      if (!this.activeSlug) {
        this.article = null;
        this.loading = false;
        return;
      }

      this.loading = true;
      try {
        // In parallel; the article renders once BOTH settle so the page
        // never flashes the wrong branch. fetchHelpContext never rejects.
        // The owner's own fee rate is fetched for the OWNER audience only
        // (shopper and superadmin readers never call it) and is optional:
        // `null` leaves the key absent so the authored fallback shows.
        const [{ data }, context, feeRate] = await Promise.all([
          api.get(`/help/${this.audience}/${this.activeSlug}`),
          fetchHelpContext(),
          this.audience === "owner"
            ? fetchEffectiveFeeRate()
            : Promise.resolve(null),
        ]);
        this.helpContext =
          feeRate === null
            ? context
            : { ...(context || {}), platform_fee_rate: feeRate };
        this.article = data;
      } catch (error) {
        this.article = null;
        if (error?.response?.status === 404) {
          this.notFound = true;
        } else {
          this.failed = true;
        }
      } finally {
        this.loading = false;
      }

      // The body only exists now, so this is the earliest point a fragment
      // can resolve to anything. After `loading = false` so the nextTick
      // inside lands after the render that creates the body element.
      if (this.article) this.scrollToHash();
    },
  },
};
</script>

<style scoped>
.help-renderer {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  padding: 1.5rem;
}

.help-nav {
  flex: 0 0 15rem;
  position: sticky;
  top: 1.5rem;
}

.help-nav__heading {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.help-nav__list,
.help-index__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.help-nav__list li {
  margin-bottom: 0.35rem;
}

.help-nav__link--active {
  font-weight: 700;
}

.help-main {
  flex: 1 1 auto;
  min-width: 0;
}

/*
 * Standalone reading measure.
 *
 * With the sidebar gone (the standalone presentation drops it), nothing
 * constrains `.help-main` any more -- `flex: 1 1 auto` lets prose run the
 * full width of the viewport, which on a wide display is a line length no
 * one can track. The sidebar was doing this job incidentally; once it is
 * conditional, the measure has to be stated.
 *
 * `ch` because the constraint is a character count, not a pixel width, so
 * it follows the reader's font size. Centred so the column does not hug
 * the left edge of an otherwise empty page -- the "static document" shape
 * Jake asked for.
 *
 * Applied via a modifier class rather than `.help-nav:not(...)` sibling
 * selectors so the rule reads the same way the template does, and so it
 * cannot be silently inverted by a future markup reorder.
 */
.help-renderer--standalone .help-main {
  /* A PERCENTAGE of the parent, not a `ch` measure (Jake, 2026-08-20).
     A character count is fixed to the font and ignores its container, so it
     reads differently in the flyover than on a full page; a percentage
     scales with whatever is actually holding it. */
  max-width: 80%;
  margin: 0 auto;
}

.help-breadcrumb {
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.help-breadcrumb__sep {
  margin: 0 0.4rem;
}

.help-article__summary {
  font-size: 1.05rem;
  opacity: 0.8;
  margin-bottom: 1.25rem;
}

.help-article__body :deep(h1),
.help-article__body :deep(h2),
.help-article__body :deep(h3) {
  margin: 1.5rem 0 0.5rem;
}

/*
 * Heading anchor affordance.
 *
 * Every heading carries a slug `id` from the server, which makes it
 * linkable but INVISIBLE -- a reader has no way to discover the link
 * exists. This is the discovery affordance: a link glyph that appears in
 * the margin on hover/focus, the convention used by essentially every
 * documentation site, so it needs no explanation.
 *
 * WHY PURE CSS AND NOT AN EMITTED <a>. The obvious implementation is for
 * the renderer to emit `<a class="anchor" href="#slug">` inside each
 * heading -- but `class` is not on the sanitizer allowlist, and this
 * change's whole constraint is that the ONE widening is `id` on `h1`-`h6`.
 * Adding `class` (or `href` on a link the reader did not author) to buy a
 * visual flourish would trade a security boundary for decoration. A
 * `::after` on `h*[id]` gets the same affordance out of markup that is
 * already there, and matches how the rest of this block styles v-html
 * content -- structurally, with no classes (see the list-indent rule
 * below, and its comment on selector scope).
 *
 * The trade-off, stated: `::after` content is not clickable and not in the
 * accessibility tree, so this is a DISCOVERY cue, not an activation
 * control -- it tells a reader the section is addressable, and the address
 * is the heading's id. A clickable per-heading copy-link control is a
 * reasonable follow-up, and it is the thing that would justify revisiting
 * the allowlist; it is deliberately not bundled into a sanitizer change.
 *
 * `:is()` keeps this to one rule for all six levels. Scoped to
 * `.help-article__body` so it cannot reach the nav or index headings.
 */
.help-article__body :deep(:is(h1, h2, h3, h4, h5, h6)[id]) {
  position: relative;
}

.help-article__body :deep(:is(h1, h2, h3, h4, h5, h6)[id])::after {
  content: "#";
  position: absolute;
  /* Sits in the gutter to the LEFT of the heading, so revealing it never
     reflows the prose -- the heading must not shift under the pointer. */
  right: 100%;
  padding-right: 0.35rem;
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
  color: var(--tf-fg-4, #6b7a86);
  font-weight: 400;
}

.help-article__body :deep(:is(h1, h2, h3, h4, h5, h6)[id]):hover::after,
.help-article__body :deep(:is(h1, h2, h3, h4, h5, h6)[id]):focus-within::after {
  opacity: 1;
}

/* The gutter does not exist on a narrow screen, so the glyph would be
   clipped off the edge. Hidden rather than repositioned into the text. */
@media (max-width: 700px) {
  .help-article__body :deep(:is(h1, h2, h3, h4, h5, h6)[id])::after {
    content: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .help-article__body :deep(:is(h1, h2, h3, h4, h5, h6)[id])::after {
    transition: none;
  }
}

/*
 * Collapsible sections (`::: details` in help markdown).
 *
 * The section heading sits INSIDE the <summary>. A block heading would push
 * the disclosure triangle onto a line of its own above it, so the heading
 * goes inline beside the marker. Its anchor `id` and the `#` affordance
 * above still apply.
 */
.help-article__body :deep(summary) {
  cursor: pointer;
  margin: 1.5rem 0 0.5rem;
}

.help-article__body :deep(summary > :is(h1, h2, h3, h4, h5, h6)) {
  display: inline;
  margin: 0;
}

.help-article__body :deep(p),
.help-article__body :deep(ul),
.help-article__body :deep(ol) {
  margin-bottom: 1rem;
  line-height: 1.6;
}

/*
 * Article-body list indentation.
 *
 * The nav/index lists above are reset to `list-style: none; padding: 0`
 * because they render links. The article body is the opposite case: its
 * `<ul>`/`<ol>` arrive as sanitized markup through `v-html` and DO want
 * their markers. With the app's global reset zeroing UA list padding, and
 * the default `list-style-position: outside`, those markers rendered in
 * the gutter to the LEFT of the prose column -- the "numbers and bullets
 * to the left of the left margin" from Jake's review.
 *
 * `padding-inline-start` (not `padding-left`) so the indent follows the
 * writing direction. `list-style-position` is deliberately left at
 * `outside`: `inside` would also stop the overhang, but it reflows
 * wrapped lines underneath the marker instead of aligning them to the
 * text above, which reads worse on the multi-line numbered steps these
 * articles are largely made of.
 *
 * Scoped to `.help-article__body` on purpose -- a broader selector would
 * sweep the nav lists back in. `help-article-list-indent.spec.js` pins
 * both the indent and that scope.
 */
.help-article__body :deep(ul),
.help-article__body :deep(ol) {
  padding-inline-start: 1.5rem;
}

/* Nested lists indent from their parent item, not from the column edge. */
.help-article__body :deep(li > ul),
.help-article__body :deep(li > ol) {
  margin-bottom: 0.25rem;
  margin-top: 0.25rem;
}

.help-article__body :deep(li) {
  margin-bottom: 0.35rem;
}

.help-article__body :deep(pre) {
  padding: 0.75rem;
  overflow-x: auto;
}

.help-index__summary {
  margin: 0.15rem 0 0.75rem;
  opacity: 0.75;
}

@media (max-width: 900px) {
  .help-renderer {
    flex-direction: column;
  }

  .help-nav {
    position: static;
    flex-basis: auto;
  }
}
</style>
